import { browser } from '$app/environment';
import { PUBLIC_CRISP_ID } from '$env/static/public';
import type { Subscriptions } from '$lib/models/init';
import type { User } from '$lib/models/user';
import { writable } from 'svelte/store';

class Crisp {
  public loading = writable(false);

  public init(locale: string, subscriptions: Subscriptions, user: User | null | void) {
    if (!browser) {
      return;
    }

    if (!window.$crisp) {
      window.$crisp = [];
      window.CRISP_WEBSITE_ID = PUBLIC_CRISP_ID;
      window.CRISP_RUNTIME_CONFIG = { locale };
      this.push(['safe', true]);
      this.push(['config', 'container:index', [99999]]);
      this.push(['do', 'chat:hide']);
      this.push([
        'on',
        'session:loaded',
        () => {
          this.loading.set(false);
        },
      ]);
      this.push(['on', 'chat:closed', () => this.hideChat()]);
      this.setSubscriptionPrices(subscriptions);
      if (user) {
        this.setUser(user);
      }
    }
  }

  public hideChat() {
    if (!browser) {
      return;
    }
    this.push(['do', 'chat:hide']);
  }

  public openChat(message: string = '') {
    if (!browser) {
      return;
    }

    this.push(['do', 'chat:show']);
    this.push(['do', 'chat:open']);

    if (message) {
      this.push(['do', 'message:show', ['text', message]]);
    }
    this.loadScript();
  }

  private loadScript() {
    if (!document.getElementById('crisp-script')) {
      this.loading.set(true);
      const script = document.createElement('script');
      script.src = 'https://client.crisp.chat/l.js';
      script.async = true;
      script.id = 'crisp-script';
      document.getElementsByTagName('head')[0].appendChild(script);
    }
  }

  setUser(user: User) {
    if (!browser) {
      return;
    }
    this.push(['set', 'user:email', user.email]);
    this.push(['set', 'user:nickname', user.displayName]);
  }

  setMessage(message: string) {
    // This line sends a message to the chatbox
    // The first parameter is the message content, and the second one is the origin ('user' or 'operator').
    // Please replace 'Sample message' with your desired message.
    this.push(['do', 'message:send', ['text', message, 'user']]);
  }

  setSubscriptionPrices(subscriptions: Subscriptions) {
    if (!browser) {
      return;
    }
    this.push([
      'set',
      'session:data',
      [
        [
          ['business-monthly', subscriptions.business.monthly.price],
          ['business-yearly', subscriptions.business.annually.monthlyPrice],
          ['pro-monthly', subscriptions.pro.monthly.price],
          ['pro-yearly', subscriptions.pro.annually.monthlyPrice],
        ],
      ],
    ]);
  }

  push(args: unknown[]) {
    if (browser && window?.$crisp) {
      window.$crisp.push(args);
    }
  }
}

export const crisp = new Crisp();
